/**
 * Service pour la gestion des permissions multi-tenant.
 * Ce service gère les permissions basées sur la nouvelle architecture multi-tenant.
 */
import api from './api';
import { 
  Permission, 
  PermissionCode,
  PlatformRoleName,
  OperationalRoleName,
  PLATFORM_ROLES,
  OPERATIONAL_ROLES
} from '@/types/permissions';

interface PlatformRole {
  id: number;
  name: PlatformRoleName;
  display_name: string;
  description: string;
  role_type: 'PLATFORM' | 'OPERATIONAL';
  level: number;
  is_system_role: boolean;
  is_active: boolean;
}

interface UserSiteAssignment {
  id: number;
  user: number;
  site: number;
  role: number;
  session_type: string;
  is_active: boolean;
  is_default: boolean;
}

interface TenantInfo {
  id: number;
  name: string;
  code: string;
  is_active: boolean;
}

interface SiteInfo {
  id: number;
  name: string;
  code: string;
  site_type: string;
  tenant: TenantInfo;
}

class MultiTenantPermissionService {
  private userPermissions: Permission[] = [];
  private userRoles: PlatformRole[] = [];
  private userPermissionCodes: string[] = [];
  private userSiteAssignments: UserSiteAssignment[] = [];
  private currentTenant: TenantInfo | null = null;
  private currentSite: SiteInfo | null = null;
  private isInitialized = false;

  // ============================================================================
  // INITIALISATION
  // ============================================================================

  /**
   * Initialise les permissions de l'utilisateur
   */
  async initializeUserPermissions(): Promise<void> {
    try {
      
      // Vérifier si déjà initialisé
      if (this.isInitialized) {
        return;
      }
      
      // Vérifier si l'utilisateur est authentifié
      const token = localStorage.getItem('access_token');
      if (!token) {
        this.isInitialized = false;
        return;
      }
      
      // Récupérer les permissions de l'utilisateur
      const permissionsUrl = '/permissions/user-permissions/my_permissions/';
      const permissionsResponse = await api.get(permissionsUrl);
      this.userPermissions = permissionsResponse.data.permissions || [];
      this.userPermissionCodes = permissionsResponse.data.permission_codes || [];
      
      // Récupérer les rôles de l'utilisateur
      const rolesUrl = '/permissions/user-permissions/my_roles/';
      const rolesResponse = await api.get(rolesUrl);
      this.userRoles = rolesResponse.data.roles || [];
      
      this.isInitialized = true;
    } catch (error: any) {
      
      // Si c'est une erreur d'authentification, ne pas lever l'erreur
      if (error?.response?.status === 401) {
        console.warn('⚠️ Erreur d\'authentification, permissions non initialisées');
        this.isInitialized = false;
        return;
      }
      
      this.isInitialized = false;
      throw error;
    }
  }

  /**
   * Rafraîchit les permissions de l'utilisateur
   */
  async refreshUserPermissions(): Promise<void> {
    await this.initializeUserPermissions();
  }

  // ============================================================================
  // VÉRIFICATIONS DE PERMISSIONS
  // ============================================================================

  /**
   * Vérifie si l'utilisateur a une permission spécifique
   */
  hasPermission(permissionCode: PermissionCode): boolean {
    if (!this.isInitialized) {
      console.warn('⚠️ Permissions non initialisées');
      return false;
    }
    if (this.userRoles.some(role => role.name === PLATFORM_ROLES.PLATFORM_SUPER_ADMIN)) {
      return true;
    }
    return this.userPermissionCodes.includes(permissionCode);
  }

  /**
   * Vérifie si l'utilisateur a au moins une des permissions spécifiées
   */
  hasAnyPermission(permissionCodes: PermissionCode[]): boolean {
    return permissionCodes.some(code => this.hasPermission(code));
  }

  /**
   * Vérifie si l'utilisateur a toutes les permissions spécifiées
   */
  hasAllPermissions(permissionCodes: PermissionCode[]): boolean {
    return permissionCodes.every(code => this.hasPermission(code));
  }

  /**
   * Vérifie si l'utilisateur a un rôle spécifique
   */
  hasRole(roleName: PlatformRoleName | OperationalRoleName): boolean {
    if (!this.isInitialized) {
      console.warn('⚠️ Permissions non initialisées');
      return false;
    }
    return this.userRoles.some(role => role.name === roleName);
  }

  /**
   * Vérifie si l'utilisateur a au moins un des rôles spécifiés
   */
  hasAnyRole(roleNames: (PlatformRoleName | OperationalRoleName)[]): boolean {
    return roleNames.some(roleName => this.hasRole(roleName));
  }

  /**
   * Vérifie si l'utilisateur a tous les rôles spécifiés
   */
  hasAllRoles(roleNames: (PlatformRoleName | OperationalRoleName)[]): boolean {
    return roleNames.every(roleName => this.hasRole(roleName));
  }

  // ============================================================================
  // VÉRIFICATIONS DE RÔLES PLATEFORME
  // ============================================================================

  /**
   * Vérifie si l'utilisateur est un super administrateur de plateforme
   */
  isPlatformSuperAdmin(): boolean {
    return this.hasRole(PLATFORM_ROLES.PLATFORM_SUPER_ADMIN);
  }

  /**
   * Vérifie si l'utilisateur est un administrateur de plateforme
   */
  isPlatformAdmin(): boolean {
    return this.hasRole(PLATFORM_ROLES.PLATFORM_ADMIN);
  }

  /**
   * Vérifie si l'utilisateur est un support de plateforme
   */
  isPlatformSupport(): boolean {
    return this.hasRole(PLATFORM_ROLES.PLATFORM_SUPPORT);
  }

  /**
   * Vérifie si l'utilisateur a des droits de plateforme
   */
  hasPlatformRights(): boolean {
    return this.isPlatformSuperAdmin() || this.isPlatformAdmin() || this.isPlatformSupport();
  }

  // ============================================================================
  // VÉRIFICATIONS DE RÔLES OPÉRATIONNELS
  // ============================================================================

  /**
   * Vérifie si l'utilisateur est un propriétaire de tenant
   */
  isTenantOwner(): boolean {
    return this.hasRole(OPERATIONAL_ROLES.TENANT_OWNER);
  }

  /**
   * Vérifie si l'utilisateur est un administrateur de tenant
   */
  isTenantAdmin(): boolean {
    return this.hasRole(OPERATIONAL_ROLES.TENANT_ADMIN);
  }

  /**
   * Vérifie si l'utilisateur est un gestionnaire de tenant
   */
  isTenantManager(): boolean {
    return this.hasRole(OPERATIONAL_ROLES.TENANT_MANAGER);
  }

  /**
   * Vérifie si l'utilisateur est un administrateur de site
   */
  isSiteAdmin(): boolean {
    return this.hasRole(OPERATIONAL_ROLES.SITE_ADMIN);
  }

  /**
   * Vérifie si l'utilisateur est un gestionnaire de site
   */
  isSiteManager(): boolean {
    return this.hasRole(OPERATIONAL_ROLES.SITE_MANAGER);
  }

  /**
   * Vérifie si l'utilisateur est un superviseur de site
   */
  isSiteSupervisor(): boolean {
    return this.hasRole(OPERATIONAL_ROLES.SITE_SUPERVISOR);
  }

  /**
   * Vérifie si l'utilisateur est un agent qualité
   */
  isQualityAgent(): boolean {
    return this.hasRole(OPERATIONAL_ROLES.QUALITY_AGENT);
  }

  /**
   * Vérifie si l'utilisateur est un opérateur
   */
  isOperator(): boolean {
    return this.hasRole(OPERATIONAL_ROLES.OPERATOR);
  }

  /**
   * Vérifie si l'utilisateur est un gestionnaire de stock
   */
  isStoreManager(): boolean {
    return this.hasRole(OPERATIONAL_ROLES.STORE_MANAGER);
  }

  /**
   * Vérifie si l'utilisateur est un consultant (lecture seule)
   */
  isViewer(): boolean {
    return this.hasRole(OPERATIONAL_ROLES.VIEWER);
  }

  // ============================================================================
  // VÉRIFICATIONS DE CONTEXTE MULTI-TENANT
  // ============================================================================

  /**
   * Vérifie si l'utilisateur peut gérer les tenants
   */
  canManageTenants(): boolean {
    return this.isPlatformSuperAdmin() || this.isPlatformAdmin();
  }

  /**
   * Vérifie si l'utilisateur peut gérer les sites
   */
  canManageSites(): boolean {
    return this.isPlatformSuperAdmin() || 
           this.isPlatformAdmin() || 
           this.isTenantOwner() || 
           this.isTenantAdmin();
  }

  /**
   * Vérifie si l'utilisateur peut gérer les utilisateurs du tenant
   */
  canManageTenantUsers(): boolean {
    return this.isPlatformSuperAdmin() || 
           this.isPlatformAdmin() || 
           this.isTenantOwner() || 
           this.isTenantAdmin() || 
           this.isSiteAdmin();
  }

  /**
   * Vérifie si l'utilisateur peut effectuer des pesées
   */
  canPerformWeighing(): boolean {
    return this.hasPermission('weighing.create') || 
           this.hasPermission('weighing.edit') ||
           this.isOperator() ||
           this.isSiteSupervisor() ||
           this.isSiteManager() ||
           this.isSiteAdmin() ||
           this.isTenantManager() ||
           this.isTenantAdmin() ||
           this.isTenantOwner();
  }

  /**
   * Vérifie si l'utilisateur peut gérer le stock
   */
  canManageStock(): boolean {
    return this.hasPermission('stock.create') || 
           this.hasPermission('stock.edit') ||
           this.isStoreManager() ||
           this.isSiteManager() ||
           this.isSiteAdmin() ||
           this.isTenantManager() ||
           this.isTenantAdmin() ||
           this.isTenantOwner();
  }

  /**
   * Vérifie si l'utilisateur peut effectuer des analyses qualité
   */
  canPerformQualityAnalysis(): boolean {
    return this.hasPermission('quality_analysis.create') || 
           this.hasPermission('quality_analysis.edit') ||
           this.isQualityAgent() ||
           this.isSiteSupervisor() ||
           this.isSiteManager() ||
           this.isSiteAdmin() ||
           this.isTenantManager() ||
           this.isTenantAdmin() ||
           this.isTenantOwner();
  }

  // ============================================================================
  // INFORMATIONS DE CONTEXTE
  // ============================================================================

  /**
   * Récupère les permissions de l'utilisateur
   */
  getUserPermissions(): Permission[] {
    return this.userPermissions;
  }

  /**
   * Récupère les rôles de l'utilisateur
   */
  getCurrentUserRoles(): PlatformRole[] {
    return this.userRoles;
  }

  /**
   * Récupère les codes de permissions de l'utilisateur
   */
  getUserPermissionCodes(): string[] {
    return this.userPermissionCodes;
  }

  /**
   * Récupère les assignations site de l'utilisateur
   */
  getUserSiteAssignments(): UserSiteAssignment[] {
    return this.userSiteAssignments;
  }

  /**
   * Récupère les informations du tenant actuel
   */
  getCurrentTenant(): TenantInfo | null {
    return this.currentTenant;
  }

  /**
   * Récupère les informations du site actuel
   */
  getCurrentSite(): SiteInfo | null {
    return this.currentSite;
  }

  /**
   * Récupère le rôle principal de l'utilisateur pour le site actuel
   */
  getCurrentSiteRole(): PlatformRole | null {
    const defaultAssignment = this.userSiteAssignments.find(assignment => assignment.is_default);
    if (!defaultAssignment) return null;
    
    return this.userRoles.find(role => role.id === defaultAssignment.role) || null;
  }

  /**
   * Vérifie si l'utilisateur est initialisé
   */
  isUserInitialized(): boolean {
    return this.isInitialized;
  }

  // ============================================================================
  // MÉTHODES UTILITAIRES
  // ============================================================================

  /**
   * Filtre les éléments selon les permissions de l'utilisateur
   */
  filterByPermission<T extends { permission?: PermissionCode; permissions?: PermissionCode[] }>(
    items: T[]
  ): T[] {
    return items.filter(item => {
      if (item.permission) {
        return this.hasPermission(item.permission);
      }
      if (item.permissions) {
        return this.hasAnyPermission(item.permissions);
      }
      return true;
    });
  }

  /**
   * Obtient le niveau d'accès d'une permission pour un utilisateur
   */
  getPermissionAccessLevel(permissionCode: PermissionCode): string {
    const permission = this.userPermissions.find(p => p.code === permissionCode);
    return permission?.access_level || 'NONE';
  }

  /**
   * Vérifie si l'utilisateur peut effectuer une action (WRITE ou ADMIN)
   */
  canWrite(permissionCode: PermissionCode): boolean {
    const accessLevel = this.getPermissionAccessLevel(permissionCode);
    return ['WRITE', 'ADMIN'].includes(accessLevel);
  }

  /**
   * Vérifie si l'utilisateur peut administrer (ADMIN seulement)
   */
  canAdmin(permissionCode: PermissionCode): boolean {
    const accessLevel = this.getPermissionAccessLevel(permissionCode);
    return accessLevel === 'ADMIN';
  }

  /**
   * Récupère les permissions par catégorie
   */
  getPermissionsByCategory(): Record<string, Permission[]> {
    const categories: Record<string, Permission[]> = {};
    this.userPermissions.forEach(permission => {
      if (!categories[permission.category]) {
        categories[permission.category] = [];
      }
      categories[permission.category].push(permission);
    });
    return categories;
  }
}

// Instance singleton
const multiTenantPermissionService = new MultiTenantPermissionService();
export default multiTenantPermissionService;
